import { useEffect, useState } from 'react';
import { useReglages } from '../../api/reglages.jsx';
import { relevesBanqueApi } from '../../api/resources';
import { Card } from '../../components/ui/Card.jsx';
import { ouvreVisionneuse } from '../../components/Visionneuse.jsx';
import { CLE_COMPTABLE } from '../../lib/cloture';
import { fmt } from '../../lib/currency';
import { tr, trf } from '../../lib/i18n';
import { anneeDuReleve, lignesPourLaComptable, titreDuReleve } from '../../lib/releves-comptable';
import { C } from '../../theme/palette';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** « 2026-11-02T09:15:00Z » → « 02.11 », le jour où le relevé a été transmis. */
function jourCourt(instant) {
  const d = new Date(instant);
  if (Number.isNaN(d.getTime())) return '';
  return String(d.getDate()).padStart(2, '0') + '.' + String(d.getMonth() + 1).padStart(2, '0');
}

const boutonPlein = {
  border: 'none',
  borderRadius: 10,
  padding: '10px 12px',
  fontWeight: 800,
  fontSize: 12,
  background: C.teal,
  color: '#fff',
  cursor: 'pointer',
};

const boutonVide = {
  border: '2px solid ' + C.navy,
  borderRadius: 10,
  padding: '8px 12px',
  fontWeight: 800,
  fontSize: 12,
  background: '#fff',
  color: C.navy,
  cursor: 'pointer',
};

/**
 * Les relevés de banque du classeur : chaque relevé importé dans l'onglet
 * Banque, gardé tel que transmis (PDF ou camt.053), s'ouvre d'un geste, et
 * ceux qu'on coche partent à la comptable par e-mail — chaque fichier joint,
 * avec un résumé des entrées, des sorties et de ce à quoi chaque ligne a été
 * reliée.
 *
 * <p>Les relevés importés avant que le fichier soit gardé n'ont pas de
 * fichier : ils n'apparaissent pas ici (leurs mouvements restent dans
 * l'onglet Banque).
 *
 * @param {object} props propriétés
 * @param {number} props.annee année du classeur
 * @param {object[]} [props.mouvements] relevé gardé dans la société, pour le résumé
 * @param {object[]} [props.factures] factures clients
 * @param {object} [props.comptable] fiche du comptable
 * @param {object[]} [props.chantiers] chantiers
 * @param {object[]} [props.frais] dépenses
 * @param {object[]} [props.achats] factures de fournisseurs jointes
 * @returns {import('react').ReactElement} la carte des relevés
 */
export function RelevesBanque({
  annee,
  mouvements,
  factures,
  comptable,
  chantiers,
  frais,
  achats,
}) {
  const { reglageJson } = useReglages();
  const email = String(reglageJson(CLE_COMPTABLE, null)?.email || '').trim();
  const [releves, setReleves] = useState([]);
  const [choix, setChoix] = useState([]);
  const [etat, setEtat] = useState('repos');
  const [message, setMessage] = useState('');

  useEffect(() => {
    let actif = true;
    relevesBanqueApi
      .liste()
      .then((recus) => {
        if (actif) setReleves(Array.isArray(recus) ? recus : []);
      })
      .catch(() => {});
    return () => {
      actif = false;
    };
  }, []);

  const liste = releves.filter((r) => !annee || anneeDuReleve(r) === +annee);
  const coches = liste.filter((r) => choix.includes(r.id));
  const tout = liste.length > 0 && coches.length === liste.length;
  const adresseOk = EMAIL.test(email);

  const bascule = (id) =>
    setChoix((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const ouvre = (r) => {
    const adresse = relevesBanqueApi.adresseFichier(r.id);
    if (!ouvreVisionneuse({ adresse, titre: titreDuReleve(r) })) {
      window.open(adresse, '_blank', 'noopener');
    }
  };

  const envoie = async () => {
    if (!coches.length || !adresseOk || etat === 'envoi') return;
    setEtat('envoi');
    setMessage('');
    try {
      const ctx = { factures, comptable, chantiers, frais, achats };
      const ligne = await relevesBanqueApi.envoieAuComptable(
        email,
        coches.map((r) => ({
          id: r.id,
          lignes: lignesPourLaComptable(r, mouvements, ctx).slice(0, 2000),
        })),
      );
      setEtat('envoye');
      setMessage(
        trf('✓ {0} relevé(s) envoyé(s) à {1}.', coches.length, ligne?.destinataire || email),
      );
      setChoix([]);
    } catch (e) {
      setEtat('echec');
      setMessage(e?.message || tr('L’envoi a échoué.'));
    }
  };

  return (
    <Card style={{ marginBottom: 6, padding: '12px 14px' }}>
      <div data-testid="releves-banque">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: C.teal + '18',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
              flexShrink: 0,
            }}
          >
            🏦
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 800 }}>{tr('Relevés de banque')}</div>
            <div style={{ fontSize: 8.5, color: C.muted }}>
              {trf('{0} relevé(s) transmis · {1}', liste.length, annee)}
            </div>
          </div>
        </div>
        {liste.length === 0 ? (
          <div style={{ fontSize: 9.5, color: C.muted, padding: '4px 0 2px', lineHeight: 1.5 }}>
            {tr(
              'Aucun relevé gardé pour cette année. Importe ton relevé dans l’onglet 🏦 Banque : son fichier sera gardé ici.',
            )}
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '4px 0 8px' }}>
              <button
                type="button"
                onClick={envoie}
                disabled={!coches.length || !adresseOk || etat === 'envoi'}
                style={{
                  ...boutonPlein,
                  opacity: !coches.length || !adresseOk ? 0.5 : 1,
                  cursor: !coches.length || !adresseOk ? 'default' : 'pointer',
                }}
              >
                {etat === 'envoi'
                  ? '⏳ ' + tr('Envoi en cours…')
                  : trf('📤 Envoyer à la comptable ({0} choisi(s))', coches.length)}
              </button>
              <button
                type="button"
                onClick={() =>
                  setChoix((p) =>
                    tout
                      ? p.filter((id) => !liste.some((r) => r.id === id))
                      : [...new Set([...p, ...liste.map((r) => r.id)])],
                  )
                }
                style={boutonVide}
              >
                {'☑ ' + (tout ? tr('Tout décocher') : tr('Tout cocher'))}
              </button>
            </div>
            {message && (
              <div
                role="status"
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  margin: '0 0 6px',
                  color: etat === 'echec' ? C.red : C.green,
                }}
              >
                {etat === 'echec' ? '✕ ' + message : message}
              </div>
            )}
            {liste.map((r) => {
              const coche = choix.includes(r.id);
              return (
                <div
                  key={r.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    borderTop: '1px solid ' + C.border,
                    padding: '9px 0',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={coche}
                    onChange={() => bascule(r.id)}
                    aria-label={titreDuReleve(r)}
                    style={{ width: 20, height: 20, accentColor: C.teal, flex: 'none' }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: C.ink }}>
                      {titreDuReleve(r)}
                    </div>
                    <div style={{ fontSize: 10, color: C.grey }}>
                      {trf(
                        '{0} transmis le {1} · {2} ligne(s)',
                        r.format === 'PDF' ? 'PDF' : 'camt.053',
                        jourCourt(r.transmisLe),
                        r.nombreLignes || 0,
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <div style={{ color: C.green, fontWeight: 800, fontSize: 11 }}>
                      {'+ ' + fmt(+r.totalEntrees || 0)}
                    </div>
                    <div style={{ color: C.red, fontWeight: 800, fontSize: 11 }}>
                      {'− ' + fmt(+r.totalSorties || 0)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => ouvre(r)}
                    style={{
                      fontSize: 10,
                      fontWeight: 800,
                      color: C.teal,
                      border: '1.5px solid ' + C.teal,
                      borderRadius: 7,
                      padding: '4px 8px',
                      background: '#fff',
                      cursor: 'pointer',
                      flex: 'none',
                    }}
                  >
                    {tr('Ouvrir')}
                  </button>
                </div>
              );
            })}
          </>
        )}
        <div
          style={{
            fontSize: 9.5,
            color: adresseOk ? C.grey : C.amber,
            background: adresseOk ? C.navyXL : C.amberL,
            borderRadius: 10,
            padding: '8px 10px',
            marginTop: 6,
            lineHeight: 1.5,
            fontWeight: adresseOk ? 400 : 700,
          }}
        >
          {adresseOk
            ? trf(
                '📤 Part par e-mail à {0}, avec chaque relevé tel que transmis (PDF ou camt) et un résumé : entrées, sorties, et à quoi chaque ligne a été reliée.',
                email,
              )
            : tr(
                'Pas encore d’adresse e-mail pour ta comptable : saisis-la dans Comptabilité → Synthèse → Comptable / Fiduciaire (case Email).',
              )}
        </div>
      </div>
    </Card>
  );
}
